import { useMemo, useState } from "react";

import { Card } from "@/components/card";
import { SearchBar } from "@/components/search-bar";
import { Label } from "@/components/ui/label";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { PublicEvent } from "@classview/shared-types";

import { AddAttendeeModal } from "./add-attendee-modal";
import { AddOrganisationToEventForm } from "./add-organisation-to-event.form";
import { AttendeesTable } from "./attendees-table";
import { InvitedOrganisationsList } from "./invited-organisations-list";
import { RegisterRoomSystemToEventForm } from "./register-room-system-to-event.form";

type ManageEventAttendeesFormProps = {
  initialValues: PublicEvent;
};

export function ManageEventAttendeesForm({
  initialValues,
}: ManageEventAttendeesFormProps) {
  const [attendeeSearch, setAttendeeSearch] = useState("");
  const filteredAttendees = useMemo(() => {
    return (
      initialValues?.session?.attendees?.filter((attendee) => {
        const name = `${attendee.firstName} ${attendee.lastName}`.toLowerCase();
        return name.includes(attendeeSearch.toLowerCase());
      }) ?? []
    );
  }, [attendeeSearch, initialValues?.session.attendees]);

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-8 flex items-center justify-between">
        <TabNavigation
          options={[
            {
              label: "Event Details",
              to: `/events/${initialValues.id}/edit`,
            },
            {
              label: "Manage Attendees",
              to: `/events/${initialValues.id}/edit-attendees`,
            },
          ]}
        />
        <AddAttendeeModal event={initialValues} />
      </div>
      <Card>
        <Card.Content className="w-[calc(100%-8px)]">
          <div className="flex flex-col gap-4">
            {initialValues.session.guests.length > 0 ? (
              <>
                <div className="flex flex-col gap-2">
                  <Label>Search Attendees</Label>
                  <SearchBar
                    placeholder="Type attendee name"
                    search={attendeeSearch}
                    setSearch={(value) =>
                      setAttendeeSearch((value as string) || "")
                    }
                    className="w-full min-w-full"
                  />
                </div>
                {filteredAttendees.length > 0 && (
                  <AttendeesTable
                    event={initialValues}
                    attendees={filteredAttendees}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col gap-2 text-center text-sm text-muted-foreground">
                This event doesn't have any attendees yet.
              </div>
            )}
          </div>
        </Card.Content>
      </Card>
      <Card>
        <Card.Header title="Manage Attending Room Systems" />
        <Card.Content>
          <RegisterRoomSystemToEventForm event={initialValues} />
        </Card.Content>
      </Card>
      <Card>
        <Card.Header title="Invite External Organisation" />
        <Card.Content>
          <AddOrganisationToEventForm event={initialValues} />
          <InvitedOrganisationsList event={initialValues} />
        </Card.Content>
      </Card>
    </div>
  );
}
